import { useState } from "react";
import { Link } from "react-router-dom";
import { Database, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function DatabaseList({ databases, mainId, onDeleted }) {
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(false);
  const ids = databases.map((d) => d.id);
  const sel = ids.filter((id) => selected.has(id));
  const all = ids.length > 0 && sel.length === ids.length;

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const remove = async () => {
    let ok = 0;
    for (const id of sel) {
      try { await api.delete(`/databases/${id}`); ok += 1; onDeleted(id); } catch (e) { toast.error(formatError(e)); break; }
    }
    if (ok) toast.success(`${ok} database${ok === 1 ? "" : "s"} deleted`);
    setSelected(new Set());
    setConfirm(false);
  };

  if (!databases.length) return null;
  const names = databases.filter((d) => selected.has(d.id)).map((d) => d.name);
  return (
    <div className="mt-10 max-w-3xl" data-testid="home-database-list">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Your databases</h2>
        {sel.length > 0 && <>
          <span className="text-sm text-muted-foreground" data-testid="database-selected-count">{sel.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => setConfirm(true)} data-testid="delete-selected-databases-button" className="text-red-400 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={15} /> Delete selected</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="clear-database-selection-button">Clear</Button>
        </>}
      </div>
      <div className="mt-3 border rounded-lg bg-card divide-y">
        <label className="flex items-center gap-3 px-4 py-2 text-xs text-muted-foreground cursor-pointer">
          <Checkbox className="data-[state=indeterminate]:bg-primary/40" checked={all ? true : sel.length ? "indeterminate" : false} onCheckedChange={() => setSelected(all ? new Set() : new Set(ids))} data-testid="database-select-all" />
          Select all
        </label>
        {databases.map((d) => (
          <div key={d.id} className={`flex items-center gap-3 px-4 py-3 transition-colors ${selected.has(d.id) ? "bg-primary/10" : "hover:bg-secondary/40"}`} data-testid={`database-row-${d.id}`}>
            <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`database-select-${d.id}`} aria-label={`Select ${d.name}`} />
            <Database size={16} className="text-primary shrink-0" />
            <Link to={`/db/${d.id}`} className="font-medium hover:text-primary truncate" data-testid={`database-link-${d.id}`}>{d.name}</Link>
            {d.id === mainId && <span className="flex items-center gap-1 text-[10px] font-bold text-amber-400"><Star size={11} /> MAIN</span>}
            <span className="ml-auto text-xs text-muted-foreground shrink-0">{d.tables.length} tables · {d.tables.reduce((s, t) => s + t.row_count, 0).toLocaleString()} records</span>
          </div>
        ))}
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} onConfirm={remove} testId="bulk-delete-databases"
        title={`Delete ${sel.length} database${sel.length === 1 ? "" : "s"}?`}
        description={`${names.join(", ")} and all their records will be permanently removed.${sel.includes(mainId) ? " This includes your MAIN database." : ""} Invoices and customer scans are kept.`} />
    </div>
  );
}
